// VideoForge Pricing Policy v1.0 — canonical source of truth.
//
// Do NOT hardcode credits, plan tiers, or multipliers anywhere else. Import
// from this module. Mirrors PRICING_POLICY_CANONICAL.md exactly.
//
// Formula: blocks = max(1, ceil(final_duration / 30)), credits = blocks * 3 * multiplier
//          1cr = 10s, preview = 0, failure = refund
// Multipliers: 720p 1.0x, 1080p 1.25x, 4K 1.75x, language 1.0x per version

export const FORMULA = {
  blockSeconds: 30,
  creditsPerBlock: 3,
  creditSeconds: 10,
  previewCredits: 0,
  failureRefund: 100, // % refunded on failed export
};

export const MULTIPLIER: Record<string, number> = {
  "720p": 1.0,
  "1080p": 1.25,
  "4K": 1.75,
};
export const LANGUAGE_MULTIPLIER = 1.0;

export const FOUNDING_CHECKOUT_URL =
  "https://checkout.dodopayments.com/buy/pdt_0NqUWCeW5vrQA7berCDcc";

export interface Plan {
  key: string;
  name: string;
  price: string;
  priceSuffix: string;
  credits: number;
  blocks: number | null;
  minutes: number;
  resolution: string | null;
  badge: string | null;
  hero: boolean;
  once: boolean;
  desc: string;
  highlight: string | null;
  features: string[];
  checkoutUrl: string | null;
}

export const PLANS: Plan[] = [
  {
    key: "free",
    name: "Free",
    price: "$0",
    priceSuffix: "/mo",
    credits: 30,
    blocks: 10,
    minutes: 5,
    resolution: "720p",
    badge: null,
    hero: false,
    once: false,
    desc: "30 credits/mo · Up to 5 min · 10 blocks · 720p",
    highlight: null,
    features: [
      "30 credits / mo",
      "720p exports",
      "Watermark",
      "1 project",
      "No rollover",
    ],
    checkoutUrl: null,
  },
  {
    key: "creator",
    name: "Creator",
    price: "$29",
    priceSuffix: "/mo",
    credits: 240,
    blocks: 80,
    minutes: 40,
    resolution: "1080p",
    badge: null,
    hero: false,
    once: false,
    desc: "240 credits/mo · Up to 40 min · 80 blocks · 1080p",
    highlight: null,
    features: [
      "240 credits / mo",
      "1080p exports",
      "3 projects",
      "1 user",
      "Rollover 240cr",
    ],
    checkoutUrl: null,
  },
  {
    key: "pro",
    name: "Pro",
    price: "$79",
    priceSuffix: "/mo",
    credits: 900,
    blocks: 300,
    minutes: 150,
    resolution: "1080p",
    badge: "Most Popular",
    hero: true,
    once: false,
    desc: "900 credits/mo · Up to 150 min · 300 blocks · 1080p",
    highlight: null,
    features: [
      "900 credits / mo",
      "1080p priority",
      "5 brand kits",
      "Templates + batch",
      "3 users",
      "Rollover 900cr",
    ],
    checkoutUrl: null,
  },
  {
    key: "business",
    name: "Business",
    price: "$149",
    priceSuffix: "/mo",
    credits: 2400,
    blocks: 800,
    minutes: 400,
    resolution: "1080p",
    badge: null,
    hero: false,
    once: false,
    desc: "2400 credits/mo · Up to 400 min · 800 blocks · 1080p",
    highlight: null,
    features: [
      "2400 credits / mo",
      "1080p priority + team",
      "10 brand kits",
      "Client workspaces + bulk",
      "5 users (+$20 extra)",
      "Rollover 2400cr",
    ],
    checkoutUrl: null,
  },
  {
    key: "agency",
    name: "Agency",
    price: "$299",
    priceSuffix: "/mo",
    credits: 6000,
    blocks: 2000,
    minutes: 1000,
    resolution: "1080p",
    badge: null,
    hero: false,
    once: false,
    desc: "6000 credits/mo · Up to 1000 min · 2000 blocks · 1080p",
    highlight: null,
    features: [
      "6000 credits / mo",
      "1080p + 4K add-on",
      "White-label",
      "Unlimited clients + API",
      "10 users + priority",
      "Rollover 6000cr",
    ],
    checkoutUrl: null,
  },
  {
    key: "founding",
    name: "Founding Lifetime",
    price: "$499",
    priceSuffix: "/ once",
    credits: 9999,
    blocks: null,
    minutes: 1666,
    resolution: null,
    badge: "Limited - 100 spots",
    hero: false,
    once: true,
    desc: "Pay once, own forever",
    highlight: "9999 credits lifetime",
    features: [
      "9999 credits lifetime (never expires)",
      "All future updates included",
      "Founding badge + priority support",
      "MP4 download",
      "Early access to Gemini TTS",
    ],
    checkoutUrl: FOUNDING_CHECKOUT_URL,
  },
];

export const TOPUP_VALID_MONTHS = 12;

export interface Topup {
  credits: number;
  minutes: number;
  price: number;
}

export const TOPUPS: Topup[] = [
  { credits: 300, minutes: 50, price: 39 },
  { credits: 900, minutes: 150, price: 99 },
  { credits: 3000, minutes: 500, price: 249 },
];

// Spend order: monthly -> rollover -> purchased
export const SPEND_ORDER = ["monthly", "rollover", "purchased"];

export const LEGAL_TEXT =
  "Credits measure finished export time. One credit covers 10 seconds of standard output. " +
  "A 30-second render block costs 3 credits. Use Pexels, Unsplash and Pixabay assets without " +
  "per-clip charges. Drafting and editing are free; credits are used when you export a finished video.";

export function blocksForDuration(durationSeconds: number): number {
  return Math.max(1, Math.ceil(Math.max(0, Number(durationSeconds) || 0) / FORMULA.blockSeconds));
}

export function calculateCredits(durationSeconds: number, complexity: string = "1080p"): number {
  const multiplier = MULTIPLIER[complexity] ?? MULTIPLIER["720p"];
  return Math.trunc(blocksForDuration(durationSeconds) * FORMULA.creditsPerBlock * multiplier);
}
